import React from "react";
import { useLocation } from "react-router-dom";
import useMe from "../state/hooks/auth/useMe";
import { useQueryClient } from "@tanstack/react-query";
import { removeItem, LOCAL_STORAGE } from "../utils/settings/storage.settings";

interface RoleBasedAccessProps {
  children: React.ReactNode;
}

const RoleBasedAccess: React.FC<RoleBasedAccessProps> = ({ children }) => {
  const { data: userData, isSuccess, isLoading } = useMe();
  const location = useLocation();
  const queryClient = useQueryClient();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (isSuccess && userData) {
    // Get all roles from both possible locations in userData
    const roles = [
      ...(userData.data?.roles || []),
      ...(userData.user?.roles || []),
    ].map((role) => role?.name?.toLowerCase());

    const currentPath = location.pathname.toLowerCase();

    // Define role-based path permissions
    const rolePermissions = {
      super_admin: ["*"], // Super admin has access to everything
      admin: [
        "/main-users",
        "/sub-users",
        "/b2b-admins",
        "/organizations",
        "/subscriptions",
        "/b2b-subscriptions",
        "/transactions",
        "/devicelist",
        "/new-subscription",
        "/sg-template-management",
        "/push-notifications",
      ],
      translator: [
        "/translations",
        "/email-translations",
        "/notification-translations",
        "/landing-translations",
        "/subscription-discounts",
        "/one-time-email",
        "/one-time-notifications",
        "/automation",
        "/sg-template-management",
        "/push-notifications",
      ],
      educational_content:[
          "/prompts",
          "/new-prompt",
      ]
    };

    // Check if user has access to the current path
    const hasAccess = (): boolean => {
      // If user is super_admin, they have access to everything
      if (roles.includes("super_admin")) return true;

      // Check if any of the user's roles grant access to the current path
      return roles.some((role) => {
        const allowedPaths =
          rolePermissions[role as keyof typeof rolePermissions];
        if (!allowedPaths) return false;

        // If the role has '*' permission or the current path matches any allowed path
        return (
          allowedPaths.includes("*") ||
          allowedPaths.some((path) => currentPath.includes(path))
        );
      });
    };

    // If user doesn't have access, log them out
    if (!hasAccess()) {
      queryClient.clear();
      removeItem("token", LOCAL_STORAGE);
    }
  }

  return <>{children}</>;
};

export default RoleBasedAccess;
